import React from 'react'
import {
  Box,
  Button,
  Chip,
  Col,
  DialogContent,
  DialogTitle,
  InputWithTags,
  Link,
  Row,
  Tab,
  Tabs,
  TextField,
  Typography,
  RowProps,
  DialogActionsWithStepper,
  LoadingButton,
} from '@applift/factor'
import { Duplicate } from '@applift/icons'
import { SVG_HOST_URL } from 'factor'

import { copyText } from '../../utils'
import { IntegrationData } from '../../models/Pixel'
import { EMAIL_REGEX } from '../../constants/constants'

import styles from './CreateOrEditPixel.module.scss'

export interface PixelIntegrationProps {
  onClose: () => void
  pixelConversionScript: string
  onIntegrationCompletion: (integrationData: IntegrationData) => void
  isEmailBeingScheduled?: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  value: number
  index: number
}

function tabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps & RowProps>(
  (props, ref) => {
    const { children, index, value, ...other } = props
    return value === index ? (
      <Row ref={ref} aria-labelledby={`simple-tab-${index}`} {...other}>
        {children}
      </Row>
    ) : null
  }
)

TabPanel.displayName = 'TabPanel'

const codeAddingSteps = [
  '1. Copy Conversion Pixel code <script>....</script>',
  '2. Find the header of your website, or locate the header template in your CMS or web platform',
  '3. Paste the Conversion Pixel code at the bottom of the header section, just above the closing head tag.',
]

export const PixelIntegration = (props: PixelIntegrationProps) => {
  const {
    onClose,
    pixelConversionScript,
    onIntegrationCompletion,
    isEmailBeingScheduled,
  } = props

  const [visibleSectionIndex, setVisibleSectionIndex] = React.useState(0)
  const [emailText, setEmailText] = React.useState(
    `Hello! ${
      JSON.parse(localStorage.getItem('authorizedUser') ?? '')?.displayName
    } has sent you a pixel code to integrate. Please follow the steps below to integrate the pixel code.`
  )
  const [recipientEmailIds, setRecipientEmailIds] = React.useState<
    {
      label: string
      error: boolean
    }[]
  >([].map(one => ({ label: one, error: false })))
  const [emailSubject, setEmailSubject] = React.useState(
    'Integrate pixel conversion'
  )

  const dialogContentRef = React.useRef<HTMLDivElement>(null)
  const overflowContainerRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const overflowContainerClientHeight =
      overflowContainerRef.current?.clientHeight ?? 0
    const overflowContainerScrollHeight =
      overflowContainerRef.current?.scrollHeight ?? 0
    overflowContainerClientHeight < overflowContainerScrollHeight &&
      dialogContentRef.current?.style.setProperty('--height', '100vh')
  }, [])

  const onFinish = () => {
    if (recipientEmailIds.length && visibleSectionIndex === 1) {
      onIntegrationCompletion({
        recipients: recipientEmailIds.map(ids => ids.label).toString(),
        emailSubject,
        emailText,
      })
    } else {
      onClose()
    }
  }

  return (
    <>
      <DialogTitle onClose={onClose}>Pixel Integration</DialogTitle>
      <DialogContent
        ref={dialogContentRef}
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
        className={styles.height}
      >
        <>
          <Row>
            <Col xs={12}>
              <Box sx={{ borderBottom: 1, mb: 32 }}>
                <Tabs
                  value={visibleSectionIndex}
                  onChange={(_, value) => setVisibleSectionIndex(value)}
                  size="medium"
                >
                  <Tab label="Copy Pixel Code" {...tabProps(0)} />
                  <Tab label="Email Pixel Code" {...tabProps(1)} />
                </Tabs>
              </Box>
            </Col>
          </Row>
          <TabPanel
            value={visibleSectionIndex}
            index={0}
            ref={overflowContainerRef}
            xs={{ gutterDirection: 'x', gutterSize: 32 }}
            sx={{ height: 100, overflowX: 'hidden', overflowY: 'auto' }}
          >
            <Col
              xs={6}
              sx={{
                height: 100,
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
            >
              <Row>
                <Col xs={12}>
                  <Typography
                    sx={{
                      textWeight: 'demi',
                      textColor: 'neutral-1000',
                      mb: 8,

                      display: 'block',
                    }}
                  >
                    Pixel Code Preview
                  </Typography>
                </Col>
              </Row>
              <Row sx={{ height: 100, overflowX: 'hidden', overflowY: 'auto' }}>
                <Col xs={12} sx={{ height: 100, display: 'flex' }}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      bgColor: 'neutral-75',
                      py: 16,
                      px: 24,
                      height: 100,
                      overflowX: 'hidden',
                      overflowY: 'auto',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography>{pixelConversionScript}</Typography>
                  </Box>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Link
                    sx={{ textWeight: 'demi', mt: 16 }}
                    Component="button"
                    color="primary"
                    onClick={() => {
                      copyText(
                        pixelConversionScript,
                        'Pixel Code copied successfully'
                      )
                    }}
                  >
                    <Duplicate fontSize={24} sx={{ mr: 4 }} />
                    <Typography sx={{ textColor: 'primary-500' }}>
                      Copy pixel code to clipboard
                    </Typography>
                  </Link>
                </Col>
              </Row>
            </Col>
            <Col
              xs={6}
              sx={{
                height: 100,
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
            >
              <Box sx={{ mb: 8 }}>
                <Typography
                  sx={{
                    textWeight: 'demi',
                    textColor: 'neutral-1000',
                  }}
                >
                  Steps to add Conversion pixel code to website
                </Typography>
              </Box>
              {codeAddingSteps.map(step => (
                <Box key={step} sx={{ mb: 4 }}>
                  <Typography>{step}</Typography>
                </Box>
              ))}
              <img
                className={styles.instructionImage}
                src={`${SVG_HOST_URL}/misc/instruction-3.png`}
                alt="instruction"
              />
            </Col>
          </TabPanel>
          <TabPanel value={visibleSectionIndex} index={1}>
            <Col xs={12}>
              <InputWithTags
                sx={{ mb: 4 }}
                label="Email ID"
                placeholder="Enter recipient email IDs"
                value={recipientEmailIds}
                // eslint-disable-next-line
                onChange={(e, value, option, reason) => {
                  if (
                    value.length <= 15 &&
                    (reason === 'createOption' ||
                      reason === 'createMultipleOption')
                  ) {
                    value = value.reduce<{ label: string; error: boolean }[]>(
                      (prev, curr) => {
                        if (
                          prev.findIndex(one => one.label === curr.label) >= 0
                        ) {
                          return prev
                        }
                        return [...prev, curr]
                      },
                      []
                    )
                    setRecipientEmailIds(value)
                  } else if (value.length <= 15) {
                    setRecipientEmailIds(value)
                  }
                }}
                createOption={value => ({
                  label: value.trim(),
                  error:
                    // eslint-disable-next-line
                    !EMAIL_REGEX.test(value.trim()),
                })}
                renderTags={(value, getTagProps) =>
                  value.map((one, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      color={one.error ? 'error' : 'primary'}
                      key={index}
                      label={one.label}
                      clickable
                    />
                  ))
                }
                getOptionLabel={value => value.label}
                createOptionOnBlur={true}
                splitCharacter={','}
              />
              <Typography
                sx={{ ml: 8, textColor: 'neutral-600' }}
                variant="label"
              >
                Max Limit: 15 Email IDs
              </Typography>
              <TextField
                variant="outlinedDash"
                sx={{ my: 16 }}
                size="medium"
                fullWidth
                label="Email Subject"
                value={emailSubject}
                onChange={e =>
                  setEmailSubject(e.target.value.trim() ? e.target.value : '')
                }
              />
              <TextField
                variant="outlinedDash"
                sx={{ mb: 8 }}
                size="medium"
                fullWidth
                label="Email Text"
                value={emailText}
                onChange={e =>
                  setEmailText(e.target.value.trim() ? e.target.value : '')
                }
              />
              <Box
                sx={{
                  my: 8,
                  bgColor: 'neutral-75',
                  width: 'auto',
                  borderRadius: 4,
                  py: 8,
                  px: 16,
                }}
              >
                <Typography>{pixelConversionScript}</Typography>
              </Box>
              <Typography sx={{ ml: 8 }}>
                The steps to integrate pixel conversion url
              </Typography>
              {codeAddingSteps.map(step => (
                <Box key={step} sx={{ my: 2, ml: 16 }}>
                  <Typography>{step}</Typography>
                </Box>
              ))}
            </Col>
          </TabPanel>
        </>
      </DialogContent>
      <DialogActionsWithStepper maxSteps={3} activeStep={2}>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onFinish}
          disabled={
            visibleSectionIndex === 1
              ? !(
                  recipientEmailIds
                    .filter(item => !item.error)
                    .map(ids => ids.label).length ===
                    recipientEmailIds.length &&
                  recipientEmailIds.length &&
                  emailSubject
                )
              : false
          }
          loading={isEmailBeingScheduled}
        >
          Done
        </LoadingButton>
      </DialogActionsWithStepper>
    </>
  )
}
